import { detailCode, statusOf } from "@/lib/api-error";
import type { components } from "@/types/api.generated";
import apiClient from "./client";

/**
 * Registro de GAPs do Fit-to-Standard (EF-FUNC-011, RF-03): a aba depois do
 * Catálogo no nó 04 do Functional. Ler e escrever pede `consultant`.
 *
 * Aliases sobre os tipos gerados do OpenAPI, como em `wricef-decisions.ts`: o
 * contrato mora no backend.
 */
export type GapItem = components["schemas"]["GapItem"];
export type GapList = components["schemas"]["GapList"];
export type GapWrite = components["schemas"]["GapWrite"];
export type GapOrigin = GapItem["origin_type"];
export type GapWorkaroundType = GapItem["workaround_type"];
export type GapImpact = GapItem["impact"];
export type GapAcceptance = GapItem["acceptance"];

/** `GET /api/projects/{projectId}/gaps`: os GAPs pelo número, com os totais da aba. */
export async function listGaps(projectId: string): Promise<GapList> {
  const res = await apiClient.get<GapList>(`/api/projects/${projectId}/gaps`);
  return res.data;
}

/** `POST /api/projects/{projectId}/gaps`: cadastra; o número GAP-00N vem do servidor. */
export async function createGap(projectId: string, body: GapWrite): Promise<GapItem> {
  const res = await apiClient.post<GapItem>(`/api/projects/${projectId}/gaps`, body);
  return res.data;
}

/** `PATCH /api/projects/{projectId}/gaps/{gapId}`: só os campos enviados mudam. */
export async function updateGap(
  projectId: string,
  gapId: string,
  body: GapWrite,
): Promise<GapItem> {
  const res = await apiClient.patch<GapItem>(`/api/projects/${projectId}/gaps/${gapId}`, body);
  return res.data;
}

/**
 * `DELETE /api/projects/{projectId}/gaps/{gapId}`. O GAP de WRICEF que era o
 * último do objeto leva junto o motivo "Falta de aderência" da decisão.
 */
export async function deleteGap(projectId: string, gapId: string): Promise<void> {
  await apiClient.delete(`/api/projects/${projectId}/gaps/${gapId}`);
}

/** Os códigos com que as rotas recusam uma gravação (`detail.code`). */
const REFUSAL_CODES = [
  "required_field",
  "wricef_object_required",
  "wricef_object_only",
  "object_not_in_catalog",
  "not_a_deliverable",
  "gap_not_found",
] as const;

/**
 * O que deu errado: um dos códigos das rotas, `forbidden` (403: abaixo de
 * `consultant`) ou `failed`, o resto, inclusive a falha de rede.
 */
export type GapErrorCode = (typeof REFUSAL_CODES)[number] | "forbidden" | "failed";

export function gapErrorCode(error: unknown): GapErrorCode {
  if (statusOf(error) === 403) return "forbidden";
  const code = detailCode(error);
  return REFUSAL_CODES.find((known) => known === code) ?? "failed";
}
